import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom/dist";
import { all_routes } from "../../../Router/all_routes";
import { useForm } from "react-hook-form";
import httpService from "../../../services/http/settings/faq.service";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import UnlimitSubmitButton from "../../../components/ui-kit/unlimit-submit-button";
import UnlimitTextarea from "../../../components/ui-kit/unlimit-textarea";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

const FaqTranslationsForm = () => {
    const { t, i18n } = useTranslation();

    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [faq, setFaq] = useState(null);

    const [selectedLanguage] = useState(
        localStorage.getItem("selectedLanguage") || i18n.language
    );

    let supportedLanguages = useSelector(
        (state) => state.systemLanguages.SystemLangauge
    ).map((lang) => ({
        code: lang.short_code,
        name: lang.name,
    }));

    // Separate English language from other languages
    const englishLanguage = supportedLanguages.find(lang => lang.code === 'en');
    const otherLanguages = supportedLanguages.filter(lang => lang.code !== 'en');

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm();

    const getFaqDetails = async () => {
        setLoading(true);
        try {
            const response = await httpService.getFaq(id);
            if (response.status === 200) {
                setValue("en.question", response.data.data.question);
                setValue("en.answer", response.data.data.answer);
                setFaq(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching FAQ details:", error);
            Swal.fire({
                icon: "error",
                title: t("general.oops"),
                text: t("general.somethingWentWrong"),
            });
        } finally {
            setLoading(false);
        }
    };

    const getFaqTranslations = async () => {
        setLoading(true);
        try {
            const response = await httpService.getFaqTranslations(id);
            if (response.status === 200) {
                supportedLanguages.forEach((lang) => {
                    if (response.data.data && response.data.data[lang.code]) {
                        setValue(
                            `${lang.code}.question`,
                            response.data.data[lang.code].question
                        );
                        setValue(
                            `${lang.code}.answer`,
                            response.data.data[lang.code].answer
                        );
                    }
                });
            }
        } catch (error) {
            console.error("Error fetching FAQ translations:", error);
            Swal.fire({
                icon: "error",
                title: t("general.oops"),
                text: t("general.somethingWentWrong"),
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            getFaqDetails();
            getFaqTranslations();
        }
    }, [id]);

    const onSubmit = async (formData) => {
        setSubmitting(true);
        try {
            const payload = {
                data: {},
            };

            // Only include non-English languages in payload
            otherLanguages.forEach((lang) => {
                if (
                    formData[lang.code] &&
                    (formData[lang.code].question || formData[lang.code].answer)
                ) {
                    payload.data[lang.code] = {
                        question: formData[lang.code].question || "",
                        answer: formData[lang.code].answer || "",
                    };
                }
            });

            const response = await httpService.updateFaqTranslations(id, payload);

            if (response.status === 200) {
                Swal.fire({
                    icon: "success",
                    title: t("general.success"),
                    text: t("general.dataUpdatedSuccessfully"),
                });
                navigate("/faq-list");
            }
        } catch (error) {
            console.error("Error updating translations:", error);
            Swal.fire({
                icon: "error",
                title: t("general.oops"),
                text: t("general.somethingWentWrong"),
            });
        } finally {
            setSubmitting(false);
        }
    };

    const breadcrumbItems = [
        { label: "", link: "/", icon: "feather-home" },
        { label: t("inputField.label.faq"), link: "/faq-list" },
        { label: t("general.translations"), active: true },
    ];

    // Render a language row with question and answer fields
    const renderLanguageRow = (language, disabled = false) => (
        <div className="card mb-4" key={language.code}>
            <div className="card-header d-flex align-items-center">
                <h5 className="mb-0">
                    <span className="badge bg-primary me-2">
                        {language.code.toUpperCase()}
                    </span>
                    {language.name} {disabled && `(${t("general.original")})`}
                </h5>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-6">
                        <UnlimitTextarea
                            label={t("tableColumnName.question")}
                            id={`${language.code}.question`}
                            name={`${language.code}.question`}
                            register={register}
                            rows={3}
                            placeholder={t("inputField.placeholder.enterQuestion")}
                            disabled={disabled}
                        />
                    </div>
                    <div className="col-md-6">
                        <UnlimitTextarea
                            label={t("tableColumnName.answer")}
                            id={`${language.code}.answer`}
                            name={`${language.code}.answer`}
                            register={register}
                            placeholder={t("inputField.placeholder.enterAnswer")}
                            rows={3}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <div className="page-header">
                    <div className="row">
                        <div className="col">
                            <div className="page-title">
                                <h4>{t("general.translations")}</h4>
                            </div>
                            <UnlimitBreadcrumb items={breadcrumbItems} />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">{t("general.loading")}</span>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">
                            <div className="col-12">
                                {/* English language (original) at the top with disabled fields */}
                                {englishLanguage && renderLanguageRow(englishLanguage, true)}

                                {/* Divider between English and other languages */}
                                <div className="mb-4">
                                    <h5 className="text-muted">{t("general.otherLanguages")}</h5>
                                    <hr />
                                </div>

                                {/* Other languages */}
                                {otherLanguages.map((language) => renderLanguageRow(language))}

                                <div className="d-flex justify-content-end mt-4 mb-4">
                                    <Link to={all_routes.FaqTypeList} className="me-2">
                                        <button type="button" className="btn btn-cancel">
                                            {t("general.button.cancel")}
                                        </button>
                                    </Link>

                                    <UnlimitSubmitButton
                                        text={t("general.actions.save")}
                                        isLoading={submitting}
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default FaqTranslationsForm;